import React, {
	type ComponentProps,
	type ComponentType,
	type PropsWithChildren,
	useCallback,
} from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ErrorBoundary as CommonErrorBoundary } from '@atlassian/search-common/error-boundary';
import { SearchDialog } from '@atlassian/search-dialog/search-dialog';

import { ReadingAidsContainer, ReadingAidsPopupContainer } from '../ai-answer-dialog/styled';
import { logger } from '../common/utils/logger';
import { DEFAULT_GAS_CHANNEL } from '../constants';
import { isHello } from '../utils';

import { messages } from './messages';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	readingAids: {
		paddingTop: token('space.150'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.300'),
		font: token('font.body.large'),
		color: token('color.text.accent.gray.bolder'),
		maxWidth: '460px',
		maxHeight: '200px',
		overflowY: 'auto',
	},
	nonReadingAids: {
		paddingTop: token('space.250'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.300'),
		font: token('font.body'),
		color: token('color.text.accent.gray'),
		maxHeight: '417px',
		overflowY: 'auto',
		width: '100%',
	},
});

type ErrorBoundaryProps = ComponentProps<'div'> & {
	cloudId: string;
	isReadingAids?: boolean;
	wrapper?: ComponentType<PropsWithChildren<object>>;
	source?: string;
};

/**
 *	@private
 *	@deprecated Use the ErrorBoundary in common instead
 *	TODO Rewrite to use the error boundry from common
 */
export const ErrorBoundary = ({
	isReadingAids,
	wrapper,
	source,
	cloudId,
	children,
}: ErrorBoundaryProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const DefaultWrapper = isReadingAids
		? (props: { children: React.ReactNode }) => (
				<ReadingAidsPopupContainer>
					<ReadingAidsContainer>{props.children}</ReadingAidsContainer>
				</ReadingAidsPopupContainer>
			)
		: SearchDialog;
	const Wrapper = wrapper ? wrapper : DefaultWrapper;

	const logError = useCallback(
		(error: Error) => {
			// Currently we are in between deprecating UFOv1 and adopting react-UFO  for platform packages, so this is a temporary way for us to collect error logs
			// https://atlassian.slack.com/archives/C01AY8132S3/p1716509785878689
			const errorAttributes = {
				errorName: error.name,
				errorMessage: error.message,
				errorStack: error.stack?.split('\n').at(1),
				isHello: isHello(cloudId),
			};

			createAnalyticsEvent({
				action: 'failed',
				actionSubject: 'aiAnswerDialog',
				source: source || 'unknown',
				eventType: 'track',
				attributes: errorAttributes,
			}).fire(DEFAULT_GAS_CHANNEL);

			logger.error({ error: errorAttributes, functionName: 'ErrorBoundary: Depricated' });
		},
		[source, createAnalyticsEvent, cloudId],
	);

	return (
		<CommonErrorBoundary
			fallback={
				<Wrapper>
					<Box xcss={cx(styles.root, isReadingAids ? styles.root : styles.readingAids)}>
						{formatMessage(messages.error_boundary_fallback_text)}
					</Box>
				</Wrapper>
			}
			onException={logError}
		>
			{children}
		</CommonErrorBoundary>
	);
};
